import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    TextField,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { enqueueSnackbar } from 'notistack';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import { formatDicomDateTime } from '~/domain/dicom/utils';
import { ImportResult } from '~/domain/services/dicomProcessor';
import {
    computeMedianSpacing,
    computeSliceAABB,
} from '~/domain/services/assembleVolume';
import {
    renderMIPProjection,
    ProjectionResult,
} from '~/domain/services/orthographicProjection';
import { obliquityAngleDeg } from '~/utils/obliquity';
import { patientPositionFromOrientation } from '~/utils/orientation';
import { cmToMm, formatMmAsCm, mmToCm } from '~/utils/units';
import NumberInput from '~/components/Inputs/NumberInput';
import {
    validateSlicePositions,
    SliceValidationStatus,
} from '~/domain/patient/image3d';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import {
    computeGridAABB,
    estimateOutputVolume,
    executeReformat,
    MIN_THICKNESS_MM,
    MAX_THICKNESS_MM,
    MIN_FOV_MM,
    MIN_SLICES,
    PROJECTION_MAX_SIZE,
} from '~/domain/services/imageService';
import type {
    ReformatTarget,
    ResampleSize,
    ScoutAxis,
} from '~/domain/services/imageService';
import ProjectionCanvas from './ProjectionCanvas';

export type { ReformatTarget };

interface ReformatDialogProps {
    open: boolean;
    onClose: () => void;
    target: ReformatTarget;
    onReformatComplete: (importResult: ImportResult) => void;
}

export default function ReformatDialog({
    open,
    onClose,
    target,
    onReformatComplete,
}: ReformatDialogProps) {
    const intl = useIntl();
    const t = useCallback(
        (id: string, values?: Record<string, string | number>) =>
            intl.formatMessage({ id }, values),
        [intl]
    );
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const { showBusy, hideBusy } = useBusyIndicator();

    // --- Normalize source data based on target kind ---
    const {
        slices,
        // exampleSlice not destructured — unused at call site
        medianSpacing,
        defaultZRange,
        defaultFovMm,
        maxFovMm,
        seriesDesc,
        defaultOutputDesc,
        seriesInfo,
        sourceGeometry,
        sourceInPlaneSize,
    } = useMemo(() => {
        if (target.kind === 'rawSeries') {
            const series = target.series;
            const slices = series.dicomImageSlices;
            const exampleSlice = slices[0];
            const sliceAabb = computeSliceAABB(slices);
            const medianSpacing = computeMedianSpacing(slices);
            const zMin = sliceAabb.worldMin[2];
            const zMax = sliceAabb.worldMax[2];
            const extentX = sliceAabb.worldMax[0] - sliceAabb.worldMin[0];
            const extentY = sliceAabb.worldMax[1] - sliceAabb.worldMin[1];
            const maxFov = Math.max(extentX, extentY);
            const src = series.seriesModule.seriesDescription || '';
            const prefix = src
                ? src.substring(0, 30)
                : intl.formatMessage({ id: 'reformatDefaultPrefix' });

            const cols = exampleSlice?.cols ?? 512;
            const rows = exampleSlice?.rows ?? 512;

            let info = null;
            if (exampleSlice) {
                const xCm = formatMmAsCm(cols * exampleSlice.pixelSpacing[0]);
                const yCm = formatMmAsCm(rows * exampleSlice.pixelSpacing[1]);
                info = {
                    seriesUid: series.seriesInstanceUID,
                    forUid: series.frameOfReferenceUid,
                    pixelSize: `${cols} × ${rows} px`,
                    physicalSize: `${xCm} × ${yCm} cm`,
                    scanDateTime: formatDicomDateTime(
                        exampleSlice.resolvedScanDateTime
                    ),
                };
            }

            // Reuse the same validation the image loading pipeline
            // uses so the uniformity label is consistent with
            // whether the series can be reconstructed directly.
            const isTilted = exampleSlice?.tilted ?? false;
            const tiltDeg = exampleSlice
                ? obliquityAngleDeg(exampleSlice.imageOrientation)
                : 0;
            const validation = validateSlicePositions(series);
            const isUniform =
                validation.status ===
                SliceValidationStatus.CompleteReconstruction;

            const inPlaneRes = exampleSlice
                ? `${formatMmAsCm(exampleSlice.pixelSpacing[0], 3)} × ${formatMmAsCm(exampleSlice.pixelSpacing[1], 3)}`
                : null;

            return {
                slices,
                exampleSlice,
                medianSpacing,
                defaultZRange: [zMin, zMax] as [number, number],
                defaultFovMm: maxFov,
                maxFovMm: maxFov,
                seriesDesc:
                    series.seriesModule.seriesDescription ||
                    intl.formatMessage({ id: 'unknown.series' }),
                defaultOutputDesc: intl.formatMessage(
                    { id: 'reformatOutputPrefix' },
                    { prefix }
                ),
                seriesInfo: info,
                sourceGeometry: {
                    inPlaneRes,
                    sliceSpacingMm: medianSpacing,
                    isUniform,
                    isTilted,
                    tiltDeg,
                    pixelSize: `${cols} × ${rows}`,
                },
                sourceInPlaneSize: Math.max(cols, rows),
            };
        } else {
            // loadedImage
            const image = target.image;
            const grid = image.grid;
            const slices = image.imageSlices;
            const exampleSlice = image.firstSlice;
            const aabb = computeGridAABB(grid);
            const zMin = aabb.worldMin[2];
            const zMax = aabb.worldMax[2];
            const extentX = aabb.worldMax[0] - aabb.worldMin[0];
            const extentY = aabb.worldMax[1] - aabb.worldMin[1];
            const maxFov = Math.max(extentX, extentY);
            const src = image.seriesMetadata.seriesDescription || '';
            const prefix = src
                ? src.substring(0, 30)
                : intl.formatMessage({ id: 'reformatDefaultPrefix' });

            const orientation = grid.getOrientation();
            const position = patientPositionFromOrientation(orientation);

            return {
                slices,
                exampleSlice,
                medianSpacing: grid.resZ,
                defaultZRange: [zMin, zMax] as [number, number],
                defaultFovMm: maxFov,
                maxFovMm: maxFov,
                seriesDesc:
                    image.seriesMetadata.seriesDescription ||
                    intl.formatMessage({ id: 'unknown.series' }),
                defaultOutputDesc: intl.formatMessage(
                    { id: 'reformatOutputPrefix' },
                    { prefix }
                ),
                seriesInfo: null,
                sourceGeometry: {
                    inPlaneRes: `${formatMmAsCm(grid.resX, 3)} × ${formatMmAsCm(grid.resY, 3)}`,
                    sliceSpacingMm: grid.resZ,
                    isUniform: true,
                    isTilted: position === null,
                    tiltDeg: obliquityAngleDeg(orientation),
                    pixelSize: `${grid.sizeX} × ${grid.sizeY}`,
                },
                sourceInPlaneSize: Math.max(grid.sizeX, grid.sizeY),
            };
        }
    }, [target, intl]);

    // State — sliceThicknessCm and fovMm store the canonical
    // values; converted to mm at domain boundaries.
    const [resampleSize, setResampleSize] =
        useState<ResampleSize>(sourceInPlaneSize);
    const [sliceThicknessCm, setSliceThicknessCm] = useState<number>(
        mmToCm(Math.max(medianSpacing, MIN_THICKNESS_MM))
    );
    const [zRange, setZRange] = useState<[number, number]>(defaultZRange);
    const [fovMm, setFovMm] = useState(defaultFovMm);
    const [outputDescription, setOutputDescription] =
        useState(defaultOutputDesc);
    const [isProcessing, setIsProcessing] = useState(false);
    const [scoutAxis, setScoutAxis] = useState<ScoutAxis>('coronal');

    // Projections (compute both, display selected)
    const [coronalProj, setCoronalProj] = useState<ProjectionResult | null>(
        null
    );
    const [sagittalProj, setSagittalProj] = useState<ProjectionResult | null>(
        null
    );
    const [projectionsLoading, setProjectionsLoading] = useState(false);

    // Reset state when target or dialog open changes
    useEffect(() => {
        if (open) {
            setSliceThicknessCm(
                mmToCm(Math.max(medianSpacing, MIN_THICKNESS_MM))
            );
            setZRange(defaultZRange);
            setFovMm(defaultFovMm);
            setOutputDescription(defaultOutputDesc);
            setResampleSize(sourceInPlaneSize);
            setIsProcessing(false);
            setScoutAxis('coronal');
        }
    }, [
        open,
        medianSpacing,
        defaultZRange,
        defaultFovMm,
        defaultOutputDesc,
        sourceInPlaneSize,
    ]);

    // Compute projections when dialog opens
    useEffect(() => {
        if (!open || slices.length === 0) {
            return;
        }

        setProjectionsLoading(true);
        setCoronalProj(null);
        setSagittalProj(null);

        const timer = setTimeout(() => {
            try {
                setCoronalProj(renderMIPProjection(slices, 'coronal', 512));
                setSagittalProj(renderMIPProjection(slices, 'sagittal', 512));
            } catch (e) {
                console.error('Error rendering projections:', e);
            } finally {
                setProjectionsLoading(false);
            }
        }, 50);

        return () => clearTimeout(timer);
    }, [open, slices]);

    // Minimum Z extent required for MIN_SLICES slices (in mm)
    const minZExtent = useMemo(() => {
        const thMm = cmToMm(sliceThicknessCm);
        return thMm <= 0 ? MIN_SLICES * MIN_THICKNESS_MM : MIN_SLICES * thMm;
    }, [sliceThicknessCm]);

    const handleZRangeChange = useCallback(
        (_: Event, newValue: number | number[]) => {
            if (Array.isArray(newValue)) {
                const [lo, hi] = [newValue[0], newValue[1]];
                if (hi - lo < minZExtent) {
                    return;
                }
                setZRange([lo, hi]);
            }
        },
        [minZExtent]
    );

    const handleFovChange = useCallback(
        (_: Event, newValue: number | number[]) => {
            if (typeof newValue === 'number') {
                setFovMm(newValue);
            }
        },
        []
    );

    // Canvas drag callbacks
    const handleCanvasZRangeChange = useCallback(
        (newRange: [number, number]) => {
            const lo = Math.max(defaultZRange[0], newRange[0]);
            const hi = Math.min(defaultZRange[1], newRange[1]);
            if (hi - lo < minZExtent) {
                return;
            }
            setZRange([lo, hi]);
        },
        [defaultZRange, minZExtent]
    );

    const handleCanvasFovChange = useCallback(
        (newFov: number) => {
            setFovMm(Math.max(MIN_FOV_MM, Math.min(maxFovMm, newFov)));
        },
        [maxFovMm]
    );

    // Estimated output info (convert cm → mm for domain call)
    const estimatedOutput = useMemo(
        () =>
            estimateOutputVolume(
                resampleSize,
                cmToMm(sliceThicknessCm),
                zRange
            ),
        [resampleSize, sliceThicknessCm, zRange]
    );

    const handleReformat = useCallback(async () => {
        setIsProcessing(true);
        await showBusy(t('reformatting'));
        try {
            const result = await executeReformat({
                target,
                sliceThicknessMm: cmToMm(sliceThicknessCm),
                resampleSize,
                fovMm,
                zRange,
                outputDescription,
            });

            // Process through DicomProcessor (infrastructure boundary)
            for (const ds of result.dicomDatasets) {
                dicomProcessor.dicomToImageSlice(ds);
            }
            const processedItems =
                await dicomProcessor.toRepository(patientRepository);

            if (processedItems) {
                onReformatComplete(processedItems);
            }

            onClose();
        } catch (e) {
            console.error('Error during reformatting:', e);
            enqueueSnackbar(t('reformatError'), {
                variant: 'error',
            });
        } finally {
            hideBusy();
            setIsProcessing(false);
        }
    }, [
        sliceThicknessCm,
        target,
        zRange,
        fovMm,
        resampleSize,
        outputDescription,
        dicomProcessor,
        patientRepository,
        onReformatComplete,
        onClose,
        showBusy,
        hideBusy,
        t,
    ]);

    const activeProj = scoutAxis === 'coronal' ? coronalProj : sagittalProj;

    return (
        <Dialog
            data-testid="reformat-dialog"
            open={open}
            onClose={isProcessing ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle>
                {t('reformatImage')} — {seriesDesc}
            </DialogTitle>
            <DialogContent sx={{ overflowX: 'hidden' }}>
                {/* ── Source Image ── */}
                <Typography variant="subtitle2" gutterBottom>
                    {t('reformatSourceInfo')}
                </Typography>
                <Box
                    px={1.5}
                    py={1}
                    sx={{
                        backgroundColor: 'action.hover',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        wordBreak: 'break-all',
                    }}
                >
                    <div>
                        {t('slices')}: {slices.length}
                        {' · '}
                        {t('modality')}:{' '}
                        {target.kind === 'rawSeries'
                            ? target.series.seriesModule.modality
                            : target.image.seriesMetadata.modality}
                    </div>
                    <div>
                        {sourceGeometry.pixelSize} px
                        {' · '}
                        {t('reformatVoxelSize')}:{' '}
                        {sourceGeometry.inPlaneRes
                            ? `${sourceGeometry.inPlaneRes} × ${formatMmAsCm(sourceGeometry.sliceSpacingMm, 3)} cm`
                            : `${formatMmAsCm(sourceGeometry.sliceSpacingMm, 3)} cm ${t('reformatSliceSpacing')}`}
                        {' · '}
                        {sourceGeometry.isUniform
                            ? t('reformatUniformSpacing')
                            : t('reformatNonUniformSpacing')}
                        {sourceGeometry.isTilted && (
                            <>
                                {' · '}
                                {intl.formatMessage(
                                    { id: 'reformatOblique' },
                                    {
                                        angle: sourceGeometry.tiltDeg.toFixed(
                                            1
                                        ),
                                    }
                                )}
                            </>
                        )}
                    </div>
                    {seriesInfo && (
                        <>
                            <div>
                                {t('reformatPixelSize', {
                                    pixelSize: seriesInfo.pixelSize,
                                })}
                                {' · '}
                                {seriesInfo.physicalSize}
                                {' · '}
                                {t('reformatScanDateTime', {
                                    scanDateTime: seriesInfo.scanDateTime,
                                })}
                            </div>
                            <div>
                                {t('reformatSeriesUid', {
                                    uid: seriesInfo.seriesUid,
                                })}
                            </div>
                            <div>
                                {t('reformatForUid', {
                                    uid: seriesInfo.forUid,
                                })}
                            </div>
                        </>
                    )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* ── Scout + Output parameters side by side ── */}
                <Box
                    display="flex"
                    gap={2}
                    flexDirection={{
                        xs: 'column',
                        sm: 'row',
                    }}
                >
                    {/* Left: scout view */}
                    <Box flex={1} minWidth={0}>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={0.5}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                {t('scoutView')}:
                            </Typography>
                            <RadioGroup
                                row
                                value={scoutAxis}
                                onChange={(_, v) =>
                                    setScoutAxis(v as ScoutAxis)
                                }
                            >
                                <FormControlLabel
                                    value="coronal"
                                    control={<Radio size="small" />}
                                    label={t('reformatCoronal')}
                                />
                                <FormControlLabel
                                    value="sagittal"
                                    control={<Radio size="small" />}
                                    label={t('reformatSagittal')}
                                />
                            </RadioGroup>
                        </Box>

                        {projectionsLoading ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                py={8}
                            >
                                <CircularProgress size={24} />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t('reformatPreview')}...
                                </Typography>
                            </Box>
                        ) : (
                            <ProjectionCanvas
                                projection={activeProj}
                                label={
                                    scoutAxis === 'coronal'
                                        ? t('reformatCoronal')
                                        : t('reformatSagittal')
                                }
                                axis={scoutAxis}
                                zRange={zRange}
                                fovMm={fovMm}
                                maxSize={PROJECTION_MAX_SIZE}
                                onZRangeChange={handleCanvasZRangeChange}
                                onFovChange={handleCanvasFovChange}
                            />
                        )}
                    </Box>

                    {/* Right: output parameters */}
                    <Box
                        flex={0}
                        flexBasis={280}
                        flexShrink={0}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        <Typography variant="subtitle2">
                            {t('reformatOutputImage')}
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            label={t('seriesDescriptionLabel')}
                            value={outputDescription}
                            onChange={(e) =>
                                setOutputDescription(e.target.value)
                            }
                            disabled={isProcessing}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel>{t('outputSize')}</InputLabel>
                            <Select
                                data-testid="reformat-output-size"
                                value={resampleSize}
                                label={t('outputSize')}
                                onChange={(e) =>
                                    setResampleSize(
                                        Number(e.target.value) as ResampleSize
                                    )
                                }
                                disabled={isProcessing}
                            >
                                {![256, 512, 1024].includes(
                                    sourceInPlaneSize
                                ) && (
                                    <MenuItem value={sourceInPlaneSize}>
                                        {sourceInPlaneSize} ×{' '}
                                        {sourceInPlaneSize} ({t('original')})
                                    </MenuItem>
                                )}
                                <MenuItem value={256}>
                                    256 × 256
                                    {sourceInPlaneSize === 256
                                        ? ` (${t('original')})`
                                        : ''}
                                </MenuItem>
                                <MenuItem value={512}>
                                    512 × 512
                                    {sourceInPlaneSize === 512
                                        ? ` (${t('original')})`
                                        : ''}
                                </MenuItem>
                                <MenuItem value={1024}>
                                    1024 × 1024
                                    {sourceInPlaneSize === 1024
                                        ? ` (${t('original')})`
                                        : ''}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <NumberInput
                            fullWidth
                            label={t('sliceThicknessCm')}
                            value={sliceThicknessCm}
                            onChange={(v) => {
                                if (v !== null) {
                                    setSliceThicknessCm(v);
                                }
                            }}
                            min={mmToCm(MIN_THICKNESS_MM)}
                            max={mmToCm(MAX_THICKNESS_MM)}
                            step={0.01}
                            precision={3}
                            disabled={isProcessing}
                        />

                        <NumberInput
                            fullWidth
                            label={t('fieldOfViewCm')}
                            value={mmToCm(fovMm)}
                            onChange={(v) => {
                                if (v !== null) {
                                    setFovMm(cmToMm(v));
                                }
                            }}
                            min={mmToCm(MIN_FOV_MM)}
                            max={mmToCm(maxFovMm)}
                            step={0.1}
                            precision={2}
                            disabled={isProcessing}
                        />
                        <Slider
                            value={fovMm}
                            onChange={handleFovChange}
                            min={MIN_FOV_MM}
                            max={maxFovMm}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${formatMmAsCm(v, 1)} cm`}
                            disabled={isProcessing}
                            sx={(theme) => ({
                                mt: -1.5,
                                '& .MuiSlider-track': {
                                    color: theme.palette.secondary.dark,
                                },
                                '& .MuiSlider-rail': {
                                    color: theme.palette.secondary.darker,
                                },
                                '& .MuiSlider-thumb': {
                                    color: theme.palette.secondary.dark,
                                    borderColor: theme.palette.secondary.dark,
                                },
                            })}
                        />

                        <Box>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="baseline"
                            >
                                <Typography variant="body2" gutterBottom>
                                    {t('sliceRange')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatMmAsCm(zRange[0])}
                                    {' – '}
                                    {formatMmAsCm(zRange[1])} cm
                                    {' · '}
                                    {Math.max(
                                        1,
                                        Math.round(
                                            (zRange[1] - zRange[0]) /
                                                cmToMm(sliceThicknessCm)
                                        )
                                    )}{' '}
                                    {t('slices')}
                                </Typography>
                            </Box>
                            <Slider
                                value={zRange}
                                onChange={handleZRangeChange}
                                min={defaultZRange[0]}
                                max={defaultZRange[1]}
                                step={0.5}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => formatMmAsCm(v)}
                                disabled={isProcessing}
                                sx={{
                                    '& .MuiSlider-track': {
                                        color: 'rgba(0, 200, 255, 0.9)',
                                    },
                                    '& .MuiSlider-thumb': {
                                        color: 'rgba(0, 200, 255, 0.9)',
                                    },
                                }}
                            />
                        </Box>

                        <Divider />

                        {estimatedOutput && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={500}
                                >
                                    {t('reformatEstimatedOutput')}:
                                </Typography>
                                <Typography variant="body2" fontSize="0.8rem">
                                    {estimatedOutput.dimensions}
                                    {' · '}~{estimatedOutput.megabytes} MB
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    data-testid="reformat-cancel-button"
                    onClick={onClose}
                    disabled={isProcessing}
                >
                    <FormattedMessage id="cancelReformat" />
                </Button>
                <Button
                    data-testid="reformat-start-button"
                    variant="contained"
                    onClick={handleReformat}
                    disabled={isProcessing}
                    startIcon={
                        isProcessing ? (
                            <CircularProgress size={16} />
                        ) : undefined
                    }
                >
                    {isProcessing ? (
                        <FormattedMessage id="reformatting" />
                    ) : (
                        <FormattedMessage id="startReformat" />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
